import { AnimatePresence, motion } from "framer-motion";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { CheckCircle, XCircle, Info, AlertTriangle, X } from "lucide-react";
import { cn } from "@/lib/utils";
import { useUIStore } from "@/stores/useUIStore";

const ICON_MAP = {
  success: CheckCircle,
  error: XCircle,
  info: Info,
  warning: AlertTriangle,
} as const;

const STYLE_MAP = {
  success: "bg-success-50 border-success-200 text-success-800 dark:bg-success-700/20 dark:border-success-700/40 dark:text-success-400",
  error: "bg-error-50 border-error-200 text-error-800 dark:bg-error-700/20 dark:border-error-700/40 dark:text-error-400",
  info: "bg-blue-50 border-blue-200 text-blue-800 dark:bg-blue-900/30 dark:border-blue-700/40 dark:text-blue-300",
  warning: "bg-warning-50 border-warning-200 text-warning-800 dark:bg-warning-700/20 dark:border-warning-700/40 dark:text-warning-400",
} as const;

function ToastContainer() {
  const { t } = useTranslation();
  const { toasts, removeToast } = useUIStore();

  return (
    // Breakpoint is `md` to match the shell: that is where Sidebar takes over
    // from BottomNav (AppShell.tsx), so it is where the obstacles change.
    //
    // Below md: stay centred (no room beside a full-width column) but sit at
    // bottom-20, ABOVE BottomNav — at bottom-4 the toast covered the nav
    // itself, hiding navigation behind a transient message.
    //
    // md and up: bottom-right. Pages are a centred max-width column, so a
    // toast centred at every breakpoint parks on whatever that column ends
    // with — on the profile page the full-width Sign Out button, which it both
    // hid and made unclickable. `<main>` also carries md:pb-28 so content
    // never ends inside this band.
    //
    // pointer-events-none on the container so the gaps between stacked toasts
    // (and the empty box when there are none) never swallow a click meant for
    // the page; each toast opts back in so its own dismiss button still works.
    // inset-x-4 below md so a full sentence uses the width the phone has —
    // a content-sized centred box wrapped the same message into a 195x146px
    // column. From md the box is content-sized again and pinned right, with
    // each toast capped at max-w-sm.
    <div className="pointer-events-none fixed inset-x-4 bottom-20 z-[100] flex flex-col gap-2 md:inset-x-auto md:bottom-4 md:right-4">
      <AnimatePresence>
        {toasts.map((toast) => {
          const Icon = ICON_MAP[toast.type];
          return (
            <motion.div
              key={toast.id}
              initial={{ opacity: 0, y: 20, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 20, scale: 0.95 }}
              className={cn(
                // max-w-sm: the messages are full sentences, and an uncapped
                // toast grew to ~470px — wide enough to reach back across the
                // page's content column from the right edge.
                "pointer-events-auto flex max-w-sm items-center gap-3 rounded-button border px-4 py-3 shadow-lg",
                STYLE_MAP[toast.type],
              )}
              role="alert"
              aria-live="polite"
            >
              <Icon className="h-5 w-5 shrink-0" />
              <div className="min-w-0 space-y-1">
                <p className="text-sm font-medium">{toast.message}</p>
                {toast.action && (
                  <Link
                    to={toast.action.to}
                    onClick={() => removeToast(toast.id)}
                    className="inline-block text-sm font-semibold underline underline-offset-2"
                  >
                    {toast.action.label}
                  </Link>
                )}
              </div>
              <button
                onClick={() => removeToast(toast.id)}
                className="ml-2 shrink-0 rounded-full p-0.5 hover:bg-black/5"
                aria-label={t("common.dismiss")}
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </motion.div>
          );
        })}
      </AnimatePresence>
    </div>
  );
}

export { ToastContainer };
